import axios from 'axios';
import { IS_DEMO, apiUrl } from '../../apiBase';
import { resetDoc } from './docStore';

// Where every phase's answers live: on the server, shared by every admin, under the
// same keys they used when this was localStorage ("notice.runs.<docId>",
// "notice.parts.<docId>"...).
//
// Read and written SYNCHRONOUSLY, which is the whole design. The fourteen stores load
// their state inside their initializers — `createDocEntryStore((docId) => ({ snapshot:
// loadListing(docId) }))` — and an initializer cannot await. Making them async would
// push a loading state into every one of them and into every component that reads
// one, to solve a problem none of them have.
//
// So a document is hydrated ONCE, by `hydrateDoc`, before its phases render (see
// NoticeViewer, which already awaits the file itself). After that this is a plain
// in-memory map: `read` is a lookup, `write` updates it and lets the flush catch up.
// The stores kept their formats and their sync shape; only the two lines under them
// changed.

// A RUN is a version of the whole document, not of one phase.
//
// Only the page classification ever had a history, in an array under one key, and it
// bought less than it looked: re-running Phase 2 destroyed Phase 2's last answer, and
// two admins appending to that array at once lost one of the two runs whole. A run is
// a set of rows now — every phase's answers, keyed by run — so re-running one phase
// keeps the others (the new run carries them forward, server-side), and two people
// running at once get two runs instead of one survivor.
//
// The stores know none of this. They ask for a key; the run is the dimension under
// them. Which is why switching runs has to reset every store (see docStore.resetDoc):
// their snapshots are a view of a run, and the run just changed.
// createdAt is the run's UTC instant (ISO string with a 'Z'), or null — shown as a
// Paris-time tooltip on the run pager. Optional so older cached shapes still type.
export type DocRun = { id: string; seq: number; fromDev: boolean; createdAt?: string | null };

// WHICH PIPELINE'S RUNS ARE ON SCREEN, and there is only one pipeline, so it is a
// constant. It is still asked and still sent: runs are filed per mode in the database
// (notice_doc_runs.mode), and a manual read through the video pipeline that this replaced
// has runs under 'llm' with their own seq numbers. Sending 'models' is what keeps those
// out of the pager instead of interleaving two histories that each number from 1 — and
// what keeps them, unreachable but intact, rather than deleting them to simplify a
// constant.
const ACTIVE_MODE = 'models';

export const docMode = (): string => ACTIVE_MODE;

const runsOf = new Map<string, DocRun[]>();
const currentOf = new Map<string, string>();

// docId -> key -> the last value anyone read or wrote, FOR THE CURRENT RUN.
const cache = new Map<string, Map<string, unknown>>();
// Documents whose GET has landed. A read for anything else is not "empty", it is
// "unknown" — and answering it with a default would let a store persist that default
// over the real answer.
const ready = new Set<string>();
// Keys changed here and not yet sent, per document.
const dirty = new Map<string, Set<string>>();
const timers = new Map<string, number>();

// How long a burst of writes is allowed to settle before it is sent. Phase 4 saves six
// keys per step and a step lands every few seconds: without this, one run is hundreds
// of requests, each one racing the last.
const FLUSH_MS = 700;

const bucket = (docId: string): Map<string, unknown> => {
  let m = cache.get(docId);
  if (!m) {
    m = new Map();
    cache.set(docId, m);
  }
  return m;
};

// Pull one document's whole state in one request. Resolves even on failure: an
// unreachable server must leave the reader empty rather than broken, and the phases
// then read their defaults and offer to run.
export async function hydrateDoc(docId: string): Promise<void> {
  if (!docId || ready.has(docId)) return;
  // NOT ready when the fetch did not land. The difference is the whole point of the
  // flag: a document whose answers failed to load looks exactly like a document nobody
  // has run — same empty stores, same empty screen — and the stores would then persist
  // that emptiness back over the real answers as soon as anything mounted. Phase 1's
  // link field does precisely that: it keeps one blank row to paste into, so an empty
  // list writes `[{ url: '' }]`, and a manual's four listing links are gone.
  //
  // Which is not hypothetical. It is what a page refreshed DURING A DEPLOY sees: nginx
  // has no upstream for a few seconds, the state GET 502s, and the reader draws a
  // manual with nothing in it. Left unguarded, that stops being a scare and becomes a
  // loss the moment the backend comes back and the first debounce fires.
  if (!(await loadRun(docId))) return;
  // A document ALWAYS has a run. Every answer is written into one, so a document with
  // none has nowhere to put the first one — and the first thing anybody does to a new
  // manual is answer something about it. Made here rather than at the first write,
  // because a write is a debounce away from the click that caused it and would have
  // to block on this.
  if (!currentRun(docId)) await openRun(docId);
  ready.add(docId);
}

// Fetch one run's answers (the newest when `runId` is omitted) and make it the one on
// screen. The cache is REPLACED, not merged: a run is a whole document, and a key the
// new run has nothing to say about is a key that has no value in it — merging would
// leave the last run's answer showing under the new run's name.
// Returns whether the answers actually arrived — hydrateDoc gates the document's whole
// writability on it, so "I could not read this" must not come back looking like "there
// was nothing to read".
async function loadRun(docId: string, runId?: string, signal?: AbortSignal): Promise<boolean> {
  try {
    const { data } = await axios.get<{ runs: DocRun[]; current: string; state: Record<string, unknown> }>(
      `/api/notice/doc/${encodeURIComponent(docId)}/state`,
      { params: { mode: ACTIVE_MODE, ...(runId ? { run: runId } : {}) }, signal },
    );
    runsOf.set(docId, data.runs || []);
    currentOf.set(docId, data.current || '');
    const m = new Map<string, unknown>();
    for (const [k, v] of Object.entries(data.state || {})) m.set(k, v);
    cache.set(docId, m);
    dirty.delete(docId);
    return true;
  } catch (e) {
    // Superseded by a newer run selection: the caller aborted this fetch, so leave the
    // loaded run untouched — the newer load is the one that will land — and say nothing.
    if (signal?.aborted || axios.isCancel(e)) return false;
    // Leave what is there: empty reads look like a document nobody has run yet, which
    // is recoverable, and a half-filled one would not be.
    //
    // But SAY so. A document that failed to load and one that has never been run are
    // the same empty screen, and the difference is whether anything you do next will
    // be kept.
    // eslint-disable-next-line no-console
    console.warn('[notice] could not load this document\'s answers:', e);
    return false;
  }
}

// Whether this document's answers are actually loaded — which is also whether anything
// done to it will be saved (see writeState). The reader shows a banner when they are
// not, because an unsaveable document looks exactly like a working one.
export const docReady = (docId: string): boolean => ready.has(docId);

// Try the failed load again, and put the freshly loaded answers on screen when it works
// (resetDoc: every store's snapshot is a view of state it read while there was none).
export async function rehydrateDoc(docId: string): Promise<boolean> {
  await hydrateDoc(docId);
  if (!ready.has(docId)) return false;
  resetDoc(docId);
  return true;
}

export const docRuns = (docId: string): DocRun[] => runsOf.get(docId) ?? [];
export const currentRun = (docId: string): string => currentOf.get(docId) ?? '';

// Show another run. Everything on screen is a view of the run, so every store re-reads
// (resetDoc) once its answers are in — and the pending writes of the run being left go
// first, or they would land on it after the user has walked away from it.
export async function showRun(docId: string, runId: string, signal?: AbortSignal): Promise<void> {
  if (!docId || runId === currentRun(docId)) return;
  await flushDoc(docId);
  await loadRun(docId, runId, signal);
  // Aborted mid-flight: loadRun left the run as it was, so there is nothing new to show —
  // resetting the stores here would blank them to the run we never left.
  if (signal?.aborted) return;
  resetDoc(docId);
}

// Open a new run, carrying the current one's answers forward, and make it the one on
// screen. Called at the top of a fresh pipeline — a re-run is a new version of the
// document, not an edit of the last one.
//
// The copy happens server-side in one statement: a client copying key by key could be
// closed half way and leave a run that never existed.
export async function newRun(docId: string): Promise<void> {
  if (!docId) return;
  await flushDoc(docId);
  const opened = await openRun(docId);
  // Every store re-reads: the answers carried forward are the same values, but they
  // are the new run's copies of them now.
  if (opened) resetDoc(docId);
}

async function openRun(docId: string): Promise<boolean> {
  try {
    // Forked from the run ON SCREEN, not from the newest one. Someone looking at run 5
    // of 63 who runs a phase means "from here" — carrying run 63 forward instead would
    // answer a question about a document they are not looking at.
    const from = currentRun(docId);
    const { data } = await axios.post<{ id: string; seq: number }>(
      `/api/notice/doc/${encodeURIComponent(docId)}/runs`,
      null,
      { params: { mode: ACTIVE_MODE, ...(from ? { from } : {}) } },
    );
    if (!data?.id) return false;
    await loadRun(docId, data.id);
    return true;
  } catch (e) {
    // Keep writing to the run already on screen. A re-run that overwrites the last one
    // is what this app did until today; a re-run that writes nowhere is not.
    //
    // Except when there IS no run on screen — then every write from here on is refused
    // for not naming one, and the reader silently keeps nothing. That is worth a shout.
    // eslint-disable-next-line no-console
    console.warn('[notice] could not open a run; answers will not be saved:', e);
    return false;
  }
}

// Delete a run and leave the reader standing on a real one.
//
// The neighbour is chosen BEFORE the delete, from the list as it is: the run before the
// deleted one, or the one after it when there is nothing before. Only when the deleted
// run was the one on screen — deleting a run you are not looking at changes nothing about
// what you are looking at, and reloading the document to prove that would blank every
// phase for a second.
//
// The server refuses the last run of a document; that answer is thrown so the caller can
// say so rather than leaving a bin that quietly does nothing.
export async function deleteRun(docId: string, runId: string): Promise<void> {
  if (!docId || !runId) return;
  const runs = docRuns(docId);
  const idx = runs.findIndex((r) => r.id === runId);
  const neighbour = idx >= 0 ? runs[idx - 1] ?? runs[idx + 1] : undefined;
  const wasCurrent = currentRun(docId) === runId;
  // Anything this run still owes goes first — a debounced write landing after the delete
  // would recreate the very rows it just lost, under a run that no longer exists.
  await flushDoc(docId);
  await axios.delete(
    `/api/notice/doc/${encodeURIComponent(docId)}/runs/${encodeURIComponent(runId)}`,
  );
  runsOf.set(docId, runs.filter((r) => r.id !== runId));
  if (!wasCurrent) return;
  // Load the neighbour: `showRun` short-circuits when the id matches the current one, and
  // the current one is the run that just stopped existing.
  currentOf.set(docId, '');
  await loadRun(docId, neighbour?.id);
  resetDoc(docId);
}

export function readState<T>(docId: string, key: string, fallback: T): T {
  const v = bucket(docId).get(key);
  return v === undefined || v === null ? fallback : (v as T);
}

export function writeState(docId: string, key: string, value: unknown) {
  // Refused for a document whose answers never arrived. Every store reads a default
  // when the cache is empty, and a default written back is not a save — it is the
  // server's copy replaced by a screen that was never filled in. Nothing is saved
  // until a hydrate succeeds, and one is retried each time the manual is opened.
  if (!ready.has(docId)) {
    // eslint-disable-next-line no-console
    console.warn('[notice] not saving %s: this document\'s answers never loaded', key);
    return;
  }
  bucket(docId).set(key, value);
  let d = dirty.get(docId);
  if (!d) {
    d = new Set();
    dirty.set(docId, d);
  }
  d.add(key);
  const t = timers.get(docId);
  if (t !== undefined) clearTimeout(t);
  timers.set(docId, window.setTimeout(() => void flushDoc(docId), FLUSH_MS));
}

// Send the keys this browser changed — only those. The server merges, so the keys
// left out are other phases' answers, possibly another admin's, written while this tab
// was reading. Sending the whole cache would make every save a claim about the whole
// document.
export async function flushDoc(docId: string): Promise<void> {
  const t = timers.get(docId);
  if (t !== undefined) {
    clearTimeout(t);
    timers.delete(docId);
  }
  // THE DEMO KEEPS NOTHING. Everything above this line still runs — the in-memory map is
  // the truth on screen, so a part stays where it was dragged, a field keeps what was
  // typed, and nothing on the page feels held shut. Only the sending stops, and a
  // refresh brings back the run exactly as prod sent it.
  //
  // The server refuses these writes as well (backend/appenv.py), which is what actually
  // makes the demo read-only. This is so nothing here spends the visit retrying a
  // request that is designed to fail and saying so in the console each time.
  if (IS_DEMO) {
    dirty.delete(docId);
    return;
  }
  const keys = dirty.get(docId);
  if (!keys?.size) return;
  const m = bucket(docId);
  const state: Record<string, unknown> = {};
  for (const k of keys) state[k] = m.get(k) ?? null;
  // Cleared BEFORE the request: a key changed while it is in flight is dirty again,
  // and clearing afterwards would drop that change on the floor.
  dirty.delete(docId);
  try {
    await axios.put(`/api/notice/doc/${encodeURIComponent(docId)}/state`, { state, run: currentRun(docId) });
  } catch (e) {
    // Put them back so the next write's flush carries them. The cache is still the
    // truth on screen; this only means the server has not caught up yet.
    const d = dirty.get(docId) ?? new Set<string>();
    for (const k of keys) d.add(k);
    dirty.set(docId, d);
    // Said out loud every time, not once: this retries forever by design, and a
    // failure that repeats forever in silence is a reader where nothing is ever kept
    // and nothing ever says why.
    // eslint-disable-next-line no-console
    console.warn('[notice] could not save (will retry):', e);
  }
}

// A run can finish seconds before someone closes the tab, and FLUSH_MS is a debounce,
// not a promise. `pagehide` fires where `beforeunload` is unreliable (it is the one
// mobile Safari honours), and sendBeacon is the only request a closing page is
// guaranteed to finish.
if (typeof window !== 'undefined' && !IS_DEMO) {
  window.addEventListener('pagehide', () => {
    for (const [docId, keys] of dirty) {
      if (!keys.size) continue;
      const m = bucket(docId);
      const state: Record<string, unknown> = {};
      for (const k of keys) state[k] = m.get(k) ?? null;
      // A Blob, not a string: sendBeacon sends text/plain by default and Flask's
      // get_json would refuse it. And a POST, not the PUT used everywhere else,
      // because POST is the only verb sendBeacon has — the route takes both.
      // apiUrl, because sendBeacon is not axios and carries no baseURL: without it the
      // demo's last write on the way out of the page would land in the real app's
      // document — the one request in the app that could cross between them.
      navigator.sendBeacon?.(
        apiUrl(`/api/notice/doc/${encodeURIComponent(docId)}/state`),
        new Blob([JSON.stringify({ state, run: currentRun(docId) })], { type: 'application/json' }),
      );
    }
  });
}
